import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Alert, Button, Empty, Select, Skeleton, Tag, Typography } from 'antd';
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  FileTextOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { format } from 'date-fns';
import get from 'lodash.get';
import { useSelector } from 'react-redux';
import SubPageLayout from '../helpers/SubPageLayout';
import { GET } from '../../../service/base-api-service';
import { getIsAdminApi, getUser } from '../../../features/auth.store';
import { downloadHandoverPdf } from '../../../utils/report-download';
import './styles/chamber-reports.scss';

const REPORT_TYPES = [
  { value: 'after-service', label: 'After Service Report' },
  { value: 'summary', label: 'Summary Report' },
  { value: 'handover', label: 'Handover Report' },
];

const normalizeList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.list)) return payload.list;
  return payload ? [payload] : [];
};

const toDisplayDate = (report) => {
  const candidates = [
    report?.servicedDate,
    report?.serviceDate,
    report?.reportDate,
    report?.createdAt,
    report?.createdDate,
    report?.uploadedAt,
    report?.date,
    report?.lastModified,
  ].filter(Boolean);

  for (const value of candidates) {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return format(parsed, 'dd/MM/yyyy');
    }
  }

  return 'N/A';
};

const toReportTitle = (report, selectedType) => {
  return (
    report?.title ||
    report?.name ||
    report?.fileName ||
    report?.chamberName ||
    (selectedType === 'handover' ? 'Handover Report' : selectedType === 'summary' ? 'Summary Report' : 'After Service Report')
  );
};

const toReportId = (report, index) =>
  report?.id || report?.serviceFormId || report?.fileName || report?.name || `report-${index}`;

const getSiteIdFromReport = (report) =>
  get(report, 'site.id') || get(report, 'siteId') || get(report, 'site_id') || get(report, 'siteIdentifier') || '';

const ChamberReports = () => {
  const { id: chamberId } = useParams();
  const location = useLocation();
  const user = useSelector(getUser);
  const isAdminApi = useSelector(getIsAdminApi);

  const tenantIdFromUser =
    get(user, 'tenant.identifier') ||
    get(user, 'tenant.id') ||
    get(user, 'tenantIdentifier') ||
    get(user, 'tenantId') ||
    '';

  const chamberNameFromState = get(location, 'state.chamberName') || '';

  const [selectedType, setSelectedType] = useState('after-service');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingReportId, setDownloadingReportId] = useState(null);
  const [error, setError] = useState('');

  const pageSubtitle = useMemo(() => {
    const typeLabel = REPORT_TYPES.find((item) => item.value === selectedType)?.label || '';
    return chamberNameFromState ? `${chamberNameFromState} • ${typeLabel}` : typeLabel;
  }, [selectedType, chamberNameFromState]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!chamberId) return;

      setLoading(true);
      setError('');

      const query = new URLSearchParams({ type: selectedType });
      if (tenantIdFromUser) {
        query.set('tenantId', tenantIdFromUser);
      }

      const normalEndpoint = `v1/report/chambers/${chamberId}/reports?${query.toString()}`;
      const adminEndpoint = `v1/admin/report/chambers/${chamberId}/reports?${query.toString()}`;

      const endpoints = isAdminApi ? [adminEndpoint, normalEndpoint] : [normalEndpoint, adminEndpoint];

      let resolved = null;
      let lastErr = null;

      for (const endpoint of endpoints) {
        try {
          const response = await GET(endpoint);
          resolved = normalizeList(response);
          break;
        } catch (err) {
          lastErr = err;
        }
      }

      if (cancelled) return;

      if (!resolved) {
        setReports([]);
        setError(lastErr?.message || 'Unable to load chamber reports.');
      } else {
        const sorted = [...resolved].sort((a, b) => {
          const d1 = new Date(
            a?.servicedDate || a?.serviceDate || a?.reportDate || a?.createdAt || a?.createdDate || a?.uploadedAt || 0,
          ).getTime();
          const d2 = new Date(
            b?.servicedDate || b?.serviceDate || b?.reportDate || b?.createdAt || b?.createdDate || b?.uploadedAt || 0,
          ).getTime();
          return d2 - d1;
        });

        setReports(sorted);
      }

      setLoading(false);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [chamberId, selectedType, isAdminApi, tenantIdFromUser]);

  const onDownloadHandover = async (report, itemId) => {
    const siteId = getSiteIdFromReport(report);
    const tenantId = report?.tenantId || tenantIdFromUser;
    const fileName = report?.fileName || report?.name;

    if (!tenantId || !siteId || !fileName) {
      return;
    }

    setDownloadingReportId(itemId);
    await downloadHandoverPdf({
      tenantId,
      siteId,
      fileName,
      isAdmin: isAdminApi,
    });
    setDownloadingReportId(null);
  };

  return (
    <SubPageLayout title="Chamber Reports" subtitle={pageSubtitle} className="chamber-reports-layout">
      <div className="chamber-reports-page">
        <div className="chamber-reports-hero">
          <div className="chamber-reports-hero-left">
            <Tag color="blue">Chamber #{chamberId}</Tag>
            {chamberNameFromState && <Tag color="green">{chamberNameFromState}</Tag>}
          </div>
          <Link to="/chambers">
            <Button icon={<ArrowLeftOutlined />}>Back to Chambers</Button>
          </Link>
        </div>

        <div className="chamber-reports-controls">
          <div className="label">Report Type</div>
          <Select
            className="type-select"
            value={selectedType}
            options={REPORT_TYPES}
            onChange={setSelectedType}
          />
        </div>

        {error && (
          <Alert
            type="error"
            message="Unable to load reports"
            description={error}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <div className="chamber-reports-list">
          {loading ? (
            <div className="loading-state">
              <Skeleton active paragraph={{ rows: 5 }} />
            </div>
          ) : reports.length === 0 ? (
            <Empty description="No reports found for the selected report type." />
          ) : (
            reports.map((report, index) => {
              const itemId = toReportId(report, index);
              const dateValue = toDisplayDate(report);
              const itemTitle = toReportTitle(report, selectedType);
              const siteId = getSiteIdFromReport(report);
              const handoverDownloadEnabled =
                selectedType === 'handover' && Boolean((report?.tenantId || tenantIdFromUser) && siteId && (report?.fileName || report?.name));
              const serviceDownloadEnabled =
                Boolean(report?.serviceFormId) && (selectedType === 'after-service' || selectedType === 'summary');

              return (
                <div className="report-item" key={itemId}>
                  <div className="report-item-main">
                    <div className="icon-wrap">
                      <FileTextOutlined />
                    </div>

                    <div className="report-text">
                      <Typography.Text strong className="title">
                        {itemTitle}
                      </Typography.Text>
                      <div className="meta-row">
                        <span className="meta-pill">
                          <CalendarOutlined /> {dateValue}
                        </span>
                        {report?.siteName && <span className="meta-pill">Site: {report.siteName}</span>}
                        {report?.tenantName && <span className="meta-pill">Tenant: {report.tenantName}</span>}
                      </div>
                    </div>
                  </div>

                  <div className="report-item-actions">
                    {handoverDownloadEnabled ? (
                      <Button
                        icon={<DownloadOutlined />}
                        loading={downloadingReportId === itemId}
                        onClick={() => onDownloadHandover(report, itemId)}
                      >
                        Download
                      </Button>
                    ) : serviceDownloadEnabled ? (
                      <Button
                        icon={<DownloadOutlined />}
                        loading={downloadingReportId === itemId}
                        onClick={async () => {
                          setDownloadingReportId(itemId);
                          const { downloadPdf } = await import('../../../utils/report-download');
                          // Backend file endpoints are currently reversed for these two types.
                          // Keep UI labels unchanged and remap only the download route flag.
                          const useAfterServiceEndpoint = selectedType === 'summary';
                          await downloadPdf(
                            report.serviceFormId,
                            isAdminApi,
                            report?.tenantId || tenantIdFromUser,
                            useAfterServiceEndpoint,
                          );
                          setDownloadingReportId(null);
                        }}
                      >
                        Download
                      </Button>
                    ) : (
                      <Button icon={<DownloadOutlined />} disabled>
                        Action
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </SubPageLayout>
  );
};

export default ChamberReports;
